import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import {
  AccountBalanceWalletOutlined,
  PaymentsOutlined,
  CloudUploadOutlined,
  Visibility,
  VisibilityOff,
  OpenInNew,
  CheckCircleOutline,
  ErrorOutline,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import {
  UploadStatement,
  fetchFinsightsTokenUsage,
  fetchStatementProgress,
} from "../../../../../data/DynamicTable/DynamicFunctions";
import { fetchJsonDynamic } from "../../../../../data/DynamicServiceNew/jsondynamicservice";
import { customerservice } from "../../../../../config";
import { PayFinsightsDialog } from "../../../../../scenes/FinsightsManager/finsights";

const isInsufficientFunds = (error) => {
  const msg = (
    (typeof error === "string" ? error : "") ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.data?.message ||
    error?.message ||
    ""
  ).toLowerCase();
  const status = error?.response?.status;
  return (
    status === 402 ||
    msg.includes("insufficient") ||
    msg.includes("not enough token") ||
    (msg.includes("token") && msg.includes("balance")) ||
    msg.includes("low balance") ||
    msg.includes("out of token") ||
    msg.includes("top up") ||
    (msg.includes("credit") && msg.includes("insufficient"))
  );
};

const UploadStatementForm = (props) => {
  const customerId = localStorage.getItem("CustomerId") || "";
  const [customerName, setCustomerName] = useState(() => {
    const cachedId = localStorage.getItem("CustomerName_Id");
    if (cachedId && cachedId === String(customerId)) {
      return localStorage.getItem("CustomerName") || "";
    }
    return props.customerName || "";
  });
  const [loadingCustomer, setLoadingCustomer] = useState(false);

  // Form inputs
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState("");

  // Token Balance state
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [tokenBalance, setTokenBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);

  // Upload state: 'idle' | 'uploading' | 'success' | 'fail'
  const [uploadState, setUploadState] = useState("idle");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [statusSubtitle, setStatusSubtitle] = useState("streaming bytes to FinSights engine...");
  const [errorMessage, setErrorMessage] = useState("");

  const timerRef = useRef(null);
  const stopPollingRef = useRef(false);

  // Fetch Customer details if customerName not already in cache
  useEffect(() => {
    if (!customerName && customerId) {
      setLoadingCustomer(true);
      fetchJsonDynamic(
        "customersById",
        "customer",
        { id: parseInt(customerId) },
        customerservice.uri
      )
        .then((res) => {
          if (res?.results) {
            const list = JSON.parse(res.results);
            const cust = Array.isArray(list) ? list[0] : list;
            const fullName = [cust?.FirstName, cust?.OtherNames || cust?.LastName]
              .filter(Boolean)
              .join(" ")
              .trim();
            if (fullName) {
              setCustomerName(fullName);
              localStorage.setItem("CustomerName", fullName);
              localStorage.setItem("CustomerName_Id", String(customerId));
            }
          }
        })
        .catch((err) => {
          console.error("Error fetching customer name:", err);
        })
        .finally(() => {
          setLoadingCustomer(false);
        });
    }
  }, [customerId, customerName]);

  const loadBalance = async () => {
    setLoadingBalance(true);
    try {
      const record = await fetchFinsightsTokenUsage();
      setTokenBalance(record?.token ?? null);
    } catch (e) {
      setTokenBalance(null);
    } finally {
      setLoadingBalance(false);
    }
  };

  useEffect(() => {
    loadBalance();
    return () => {
      stopPollingRef.current = true; if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (selected.size > 52428800) {
      setFormError("The file exceeds the 50MB size limit.");
      setFile(null);
      return;
    }

    setFormError("");
    setFile(selected);
  };

  const handleStartUpload = async (e) => {
    if (e) e.preventDefault();

    if (tokenBalance === 0) {
      setFormError("Insufficient funds: Your FinSights token balance is depleted. Please top up before uploading.");
      setPayDialogOpen(true);
      return;
    }

    if (!file) {
      setFormError("Please select an M-Pesa statement PDF file to upload.");
      return;
    }
    if (!password.trim()) {
      setFormError("Please enter the statement PDF password.");
      return;
    }

    setFormError("");
    setUploadState("uploading");
    setUploadProgress(12);
    setStatusSubtitle("streaming encrypted payload to FinSights engine...");


    try {
      const enhancedValues = {
        file,
        password,
        customerId,
      };

      const uploadResult = await UploadStatement(enhancedValues);

      // Extract statementId from the upload response
      const statementId =
        uploadResult?.statementId ||
        uploadResult?.StatementId ||
        uploadResult?.data?.statementId ||
        uploadResult?.data?.StatementId ||
        null;

      if (statementId) {
        // Sequential polling — next request only fires after previous one returns
        stopPollingRef.current = false;
        setStatusSubtitle("FinSights AI is crunching your transactions...");
        let pollCount = 0;
        const maxPolls = 90; // 90 polls × ~4s each ≈ 6 minutes max

        const pollOnce = async () => {
          if (stopPollingRef.current) return; // cancelled (unmount / error)
          pollCount++;
          try {
            const progress = await fetchStatementProgress(statementId);
            if (stopPollingRef.current) return;

            if (progress && progress.success) {
              const pct = progress.percentage ?? 5;
              const sts = progress.status ?? "processing";
              setUploadProgress(pct);

              if (sts === "processed" || pct >= 100) {
                stopPollingRef.current = true;
                setUploadProgress(100);
                setUploadState("success");
                setStatusSubtitle("Statement fully crunched — analytics ready!");
                props.onAction?.();
                loadBalance();
                return; // done — no next poll
              }

              // Update subtitle based on progress stage
              if (pct <= 40) {
                setStatusSubtitle("FinSights AI parsing transactions & cashflows...");
              } else if (pct <= 70) {
                setStatusSubtitle(`Analysing cashflows & credit patterns (${pct}%)...`);
              } else {
                setStatusSubtitle(`Finalising risk profile & analytics (${pct}%)...`);
              }
            }
          } catch (pollErr) {
            console.warn("Progress poll error:", pollErr.message);
          }

          if (pollCount >= maxPolls) {
            stopPollingRef.current = true;
            setUploadProgress(100);
            setUploadState("success");
            setStatusSubtitle("Statement processed.");
            props.onAction?.();
            loadBalance();
            return;
          }

          // Schedule next poll only AFTER this one finished — no overlapping requests
          timerRef.current = setTimeout(pollOnce, 4000);
        };

        // Kick off first poll after a short delay (give backend time to register)
        timerRef.current = setTimeout(pollOnce, 3000);
      } else {
        setUploadProgress(100);
        setUploadState("success");
        setStatusSubtitle("Statement uploaded successfully!");
        props.onAction?.();
        loadBalance();
      }
    } catch (error) {
      stopPollingRef.current = true; clearTimeout(timerRef.current);
      setUploadState("fail");

      const rawMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.data?.message ||
        error?.message ||
        "The connection dropped or an error occurred.";

      const isDepleted = isInsufficientFunds(error);
      const subtitle = isDepleted
        ? "Insufficient funds: FinSights token balance is depleted"
        : "The connection dropped or an error occurred";

      setStatusSubtitle(subtitle);
      setErrorMessage(
        isDepleted
          ? "Insufficient funds to crunch statement. FinSights token balance is depleted. Please top up tokens to continue."
          : rawMsg
      );
    }
  };

  const handleResetToIdle = () => {
    setUploadState("idle");
    setUploadProgress(0);
    setErrorMessage("");
    setFile(null);
    setPassword("");
    setFormError("");
  };

  const handleOpenFinsights = () => {
    const raw = localStorage.getItem("token");
    if (!raw) return;
    let sababishaToken = raw;
    try {
      sababishaToken = JSON.parse(raw);
    } catch {
      sababishaToken = raw;
    }
    const url = new URL("https://finsights.sababisha.com/en/sso");
    url.searchParams.set("token", sababishaToken);
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      sx={{
        background: "radial-gradient(ellipse at 50% 0%, #163863 0%, #0e2340 50%, #0a182d 100%)",
        color: "#f8fafc",
        p: { xs: 2.5, md: 3 },
        minHeight: "100vh",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        fontFamily: "Inter, -apple-system, sans-serif",
      }}
    >
      {/* Header bar */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2.5}
        pb={2}
        borderBottom="1px solid rgba(56, 189, 248, 0.18)"
      >
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: "#f8fafc", letterSpacing: 0.5 }}>
            Upload M-Pesa Statement
          </Typography>
          <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.82rem" }}>
            {customerName ? (
              <span style={{ color: "#38bdf8", fontWeight: 600 }}>{customerName}</span>
            ) : loadingCustomer ? (
              "Loading customer details..."
            ) : (
              "FinSights Quantum Engine"
            )}
            {customerName ? " • FinSights Quantum Engine" : ""}
          </Typography>
        </Box>

        {/* Balance / Token Check Badge */}
        {loadingBalance ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={16} sx={{ color: "#38bdf8" }} />
            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
              Checking tokens…
            </Typography>
          </Box>
        ) : tokenBalance !== null && tokenBalance > 0 ? (
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              icon={
                <CheckCircleOutline
                  sx={{ color: "#22c55e !important", fontSize: "16px !important" }}
                />
              }
              label={`Continue (${tokenBalance} token${tokenBalance === 1 ? "" : "s"})`}
              size="small"
              sx={{
                bgcolor: "rgba(34, 197, 94, 0.15)",
                color: "#22c55e",
                fontWeight: 700,
                fontSize: "0.75rem",
                border: "1px solid rgba(34, 197, 94, 0.4)",
              }}
            />
            <Button
              size="small"
              startIcon={<PaymentsOutlined fontSize="small" />}
              onClick={() => setPayDialogOpen(true)}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                color: "#f8fafc",
                bgcolor: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
              }}
            >
              Top Up
            </Button>
          </Box>
        ) : tokenBalance === 0 ? (
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              icon={
                <ErrorOutline
                  sx={{ color: "#ef4444 !important", fontSize: "16px !important" }}
                />
              }
              label="Insufficient Tokens"
              size="small"
              sx={{
                bgcolor: "rgba(239, 68, 68, 0.18)",
                color: "#ef4444",
                fontWeight: 700,
                fontSize: "0.75rem",
                border: "1px solid rgba(239, 68, 68, 0.4)",
              }}
            />
            <Button
              size="small"
              variant="contained"
              startIcon={<PaymentsOutlined fontSize="small" />}
              onClick={() => setPayDialogOpen(true)}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                fontWeight: 700,
                color: "#fff",
                bgcolor: "#ef4444",
                boxShadow: "0 0 12px rgba(239, 68, 68, 0.4)",
                "&:hover": { bgcolor: "#dc2626" },
              }}
            >
              Top Up
            </Button>
          </Box>
        ) : (
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              size="small"
              startIcon={<PaymentsOutlined fontSize="small" />}
              onClick={() => setPayDialogOpen(true)}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                color: "#f8fafc",
                bgcolor: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
              }}
            >
              Top Up
            </Button>
          </Box>
        )}
      </Box>

      {/* Insufficient funds alert (only when idle) */}
      {tokenBalance === 0 && uploadState === "idle" && (
        <Alert
          severity="error"
          sx={{
            mb: 2.5,
            bgcolor: "rgba(239, 68, 68, 0.12)",
            color: "#fecaca",
            border: "1px solid rgba(239, 68, 68, 0.35)",
            "& .MuiAlert-icon": { color: "#ef4444" },
          }}
          action={
            <Button
              size="small"
              variant="contained"
              startIcon={<PaymentsOutlined />}
              onClick={() => setPayDialogOpen(true)}
              sx={{
                bgcolor: "#ef4444",
                color: "#fff",
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "0 0 14px rgba(239, 68, 68, 0.4)",
                "&:hover": { bgcolor: "#dc2626" },
              }}
            >
              Top Up Tokens
            </Button>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#fca5a5" }}>
            Insufficient Funds
          </Typography>
          <Typography variant="caption" sx={{ color: "#cbd5e1" }}>
            FinSights tokens are depleted. Please top up your balance before uploading and crunching statements.
          </Typography>
        </Alert>
      )}

      {/* -------------------- 1. LOADING STATE (AUTOMATICALLY ON UPLOAD) -------------------- */}
      {uploadState === "uploading" && (
        <Box
          sx={{
            borderRadius: "16px",
            p: { xs: 3, md: 5 },
            my: 2,
            background: "radial-gradient(ellipse at top, #184274 0%, #0e2544 80%)",
            border: "1px solid rgba(56, 189, 248, 0.35)",
            boxShadow: "0 12px 36px rgba(0, 0, 0, 0.6)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 140,
              height: 140,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "radial-gradient(circle, rgba(6,182,212,0.25) 0%, rgba(6,182,212,0.03) 70%)",
              boxShadow: "0 0 45px rgba(6, 182, 212, 0.4), inset 0 0 15px rgba(6, 182, 212, 0.2)",
              mb: 2.5,
            }}
          >
            <svg width="140" height="140" viewBox="0 0 120 120">
              <circle
                cx="60"
                cy="60"
                r="52"
                fill="none"
                stroke="rgba(6, 182, 212, 0.18)"
                strokeWidth="5.5"
              />
              <circle
                cx="60"
                cy="60"
                r="52"
                fill="none"
                stroke="#22d3ee"
                strokeWidth="5.5"
                strokeDasharray="326.7"
                strokeDashoffset={String(326.7 - (326.7 * uploadProgress) / 100)}
                strokeLinecap="round"
                transform="rotate(-90 60 60)"
                style={{ transition: "stroke-dashoffset 0.4s ease" }}
              />
              <path
                d="M 38 56 Q 48 47 60 56 T 82 56"
                fill="none"
                stroke="#22d3ee"
                strokeWidth="4"
                strokeLinecap="round"
              />
              <text
                x="60"
                y="74"
                textAnchor="middle"
                fill="#22d3ee"
                fontSize="12"
                fontWeight="800"
                fontFamily="ui-monospace, monospace"
              >
                {`${uploadProgress}%`}
              </text>
            </svg>
          </Box>

          <Typography
            variant="h6"
            fontWeight={800}
            sx={{
              color: "#f8fafc",
              letterSpacing: 2,
              fontSize: "1.05rem",
              mb: 0.5,
            }}
          >
            CRUNCHING STATEMENT
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#38bdf8",
              fontSize: "0.85rem",
              maxWidth: 420,
            }}
          >
            {statusSubtitle}
          </Typography>

          <Box mt={3} width="100%" maxWidth={320}>
            <Box
              sx={{
                width: "100%",
                height: 6,
                borderRadius: 3,
                bgcolor: "rgba(255, 255, 255, 0.08)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${uploadProgress}%`,
                  height: "100%",
                  bgcolor: "#38bdf8",
                  borderRadius: 3,
                  boxShadow: "0 0 12px #38bdf8",
                  transition: "width 0.4s ease",
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* -------------------- 2. FAIL STATE (AUTOMATICALLY ON ERROR) -------------------- */}
      {uploadState === "fail" && (
        <Box
          sx={{
            borderRadius: "16px",
            p: { xs: 3, md: 4.5 },
            my: 2,
            background: "radial-gradient(ellipse at top, #2f1727 0%, #0d213b 80%)",
            border: "1px solid rgba(244, 63, 94, 0.35)",
            boxShadow: "0 12px 36px rgba(0, 0, 0, 0.6)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          {/* FAIL Ring */}
          <Box
            sx={{
              position: "relative",
              width: 130,
              height: 130,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "radial-gradient(circle, rgba(244,63,94,0.25) 0%, rgba(244,63,94,0.03) 70%)",
              boxShadow: "0 0 45px rgba(244, 63, 94, 0.4), inset 0 0 15px rgba(244, 63, 94, 0.2)",
              mb: 2,
            }}
          >
            <svg width="130" height="130" viewBox="0 0 120 120">
              <circle
                cx="60"
                cy="60"
                r="52"
                fill="none"
                stroke="rgba(244, 63, 94, 0.2)"
                strokeWidth="5.5"
              />
              <circle
                cx="60"
                cy="60"
                r="52"
                fill="none"
                stroke="#fb7185"
                strokeWidth="5.5"
                strokeDasharray="326.7"
                strokeDashoffset="40"
                strokeLinecap="round"
                transform="rotate(-90 60 60)"
              />
              {/* Downward Arrow */}
              <path
                d="M 60 40 L 60 76 M 46 64 L 60 78 L 74 64"
                fill="none"
                stroke="#fb7185"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>

          <Typography
            variant="h6"
            fontWeight={800}
            sx={{
              color: isInsufficientFunds(errorMessage) ? "#ef4444" : "#f8fafc",
              letterSpacing: 2,
              fontSize: "1.1rem",
              mb: 0.5,
            }}
          >
            {isInsufficientFunds(errorMessage) ? "INSUFFICIENT FUNDS" : "FAIL"}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "#fb7185",
              fontSize: "0.88rem",
              fontWeight: 600,
              mb: 2.5,
            }}
          >
            {isInsufficientFunds(errorMessage)
              ? "Insufficient funds: FinSights token balance is depleted"
              : statusSubtitle}
          </Typography>

          {/* Detailed Error Box */}
          <Box
            p={2}
            mb={3}
            width="100%"
            maxWidth={480}
            borderRadius="10px"
            sx={{
              bgcolor: "rgba(244, 63, 94, 0.08)",
              border: "1px solid rgba(244, 63, 94, 0.25)",
              textAlign: "left",
            }}
          >
            <Typography variant="body2" sx={{ color: "#fecdd3", fontWeight: 600, mb: 1 }}>
              {errorMessage || "FinSights statement crunching could not proceed."}
            </Typography>

            {isInsufficientFunds(errorMessage) && (
              <Box
                p={1.5}
                mt={1.5}
                borderRadius="8px"
                sx={{ bgcolor: "#0a101d", border: "1px solid rgba(255,255,255,0.06)", fontSize: "0.8rem" }}
              >
                <Typography variant="caption" sx={{ color: "#38bdf8", fontWeight: 700, display: "block", mb: 0.5 }}>
                  📍 FinSights Tokens Depleted:
                </Typography>
                <Typography variant="caption" sx={{ color: "#cbd5e1", display: "block", lineHeight: 1.6 }}>
                  • Click <strong>"Top Up Balance Now"</strong> to trigger an M-Pesa STK push directly.<br />
                  • Or visit <strong>Company Management &rarr; FinSights</strong> to manage token allocations.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Failure Action Buttons */}
          <Box display="flex" gap={1.5} justifyContent="center" flexWrap="wrap">
            {isInsufficientFunds(errorMessage) && (
              <Button
                variant="contained"
                startIcon={<PaymentsOutlined />}
                onClick={() => setPayDialogOpen(true)}
                sx={{
                  bgcolor: "#ef4444",
                  color: "#fff",
                  fontWeight: 700,
                  textTransform: "none",
                  px: 2.5,
                  py: 1,
                  borderRadius: "8px",
                  boxShadow: "0 0 20px rgba(239, 68, 68, 0.45)",
                  "&:hover": { bgcolor: "#dc2626" },
                }}
              >
                Top Up Balance Now (M-Pesa)
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={handleResetToIdle}
              sx={{
                color: "#f8fafc",
                borderColor: "rgba(255,255,255,0.25)",
                textTransform: "none",
                fontWeight: 600,
                px: 2.5,
                py: 1,
                borderRadius: "8px",
                "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.06)" },
              }}
            >
              Try Again
            </Button>
          </Box>
        </Box>
      )}

      {/* -------------------- 3. SUCCESS STATE (AUTOMATICALLY ON SUCCESS) -------------------- */}
      {uploadState === "success" && (
        <Box
          sx={{
            borderRadius: "16px",
            p: { xs: 3, md: 4.5 },
            my: 2,
            background: "radial-gradient(ellipse at top, #14404d 0%, #0d213b 80%)",
            border: "1px solid rgba(34, 211, 238, 0.35)",
            boxShadow: "0 12px 36px rgba(0, 0, 0, 0.6)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 130,
              height: 130,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "radial-gradient(circle, rgba(34,211,238,0.25) 0%, rgba(34,211,238,0.03) 70%)",
              boxShadow: "0 0 45px rgba(34, 211, 238, 0.4), inset 0 0 15px rgba(34, 211, 238, 0.2)",
              mb: 2,
            }}
          >
            <svg width="130" height="130" viewBox="0 0 120 120">
              <circle
                cx="60"
                cy="60"
                r="52"
                fill="none"
                stroke="rgba(34, 211, 238, 0.2)"
                strokeWidth="5.5"
              />
              <circle
                cx="60"
                cy="60"
                r="52"
                fill="none"
                stroke="#22d3ee"
                strokeWidth="5.5"
                strokeDasharray="326.7"
                strokeDashoffset="0"
                strokeLinecap="round"
                transform="rotate(-90 60 60)"
              />
              <path
                d="M 38 56 Q 48 47 60 56 T 82 56"
                fill="none"
                stroke="#22d3ee"
                strokeWidth="4"
                strokeLinecap="round"
              />
              <text
                x="60"
                y="74"
                textAnchor="middle"
                fill="#22d3ee"
                fontSize="12"
                fontWeight="800"
                fontFamily="ui-monospace, monospace"
              >
                100%
              </text>
            </svg>
          </Box>

          <Typography
            variant="h6"
            fontWeight={800}
            sx={{
              color: "#f8fafc",
              letterSpacing: 2,
              fontSize: "1.1rem",
              mb: 0.5,
            }}
          >
            SUCCESS
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "#38bdf8",
              fontSize: "0.85rem",
              fontWeight: 600,
              mb: 3,
            }}
          >
            every byte arrived
          </Typography>

          <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              endIcon={<OpenInNew fontSize="small" />}
              onClick={handleOpenFinsights}
              sx={{
                bgcolor: "#0284c7",
                color: "#fff",
                fontWeight: 700,
                textTransform: "none",
                px: 2.5,
                py: 1,
                borderRadius: "8px",
                boxShadow: "0 0 20px rgba(2, 132, 199, 0.4)",
                "&:hover": { bgcolor: "#0369a1" },
              }}
            >
              View Report in FinSights
            </Button>
            <Button
              variant="outlined"
              onClick={handleResetToIdle}
              sx={{
                color: "#94a3b8",
                borderColor: "rgba(255,255,255,0.15)",
                textTransform: "none",
                fontWeight: 600,
                px: 2.5,
                py: 1,
                borderRadius: "8px",
                "&:hover": { color: "#fff", borderColor: "#fff", bgcolor: "rgba(255,255,255,0.06)" },
              }}
            >
              Upload Another Statement
            </Button>
          </Box>
        </Box>
      )}

      {/* -------------------- 4. FORM INPUTS (SHOWN ONLY WHEN IDLE) -------------------- */}
      {uploadState === "idle" && (
        <form onSubmit={handleStartUpload}>
          <Box mb={2.5}>
            {/* File Dropzone */}
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{
                p: 3,
                border: "2px dashed rgba(56, 189, 248, 0.45)",
                bgcolor: "rgba(20, 48, 84, 0.5)",
                borderRadius: "12px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                "&:hover": {
                  border: "2px dashed #38bdf8",
                  bgcolor: "rgba(30, 68, 116, 0.7)",
                },
              }}
            >
              <CloudUploadOutlined sx={{ fontSize: 36, color: "#38bdf8" }} />
              <Typography variant="body1" fontWeight={700} sx={{ color: "#f8fafc" }}>
                {file ? file.name : "Select M-Pesa statement PDF"}
              </Typography>
              <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                {file
                  ? `${(file.size / 1024).toFixed(1)} KB • Ready for crunching`
                  : "PDF format up to 50MB"}
              </Typography>
              <input
                type="file"
                accept=".pdf"
                hidden
                onChange={handleFileChange}
              />
            </Button>

            {formError && (
              <Typography variant="caption" sx={{ color: "#f43f5e", display: "block", mt: 1 }}>
                {formError}
              </Typography>
            )}
          </Box>

          {/* Password Input */}
          <Box mb={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Statement Password *"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="e.g. National ID, Phone, or Safaricom PIN"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: "#94a3b8" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "rgba(13, 31, 56, 0.8)",
                  color: "#f8fafc",
                  borderRadius: "10px",
                  "& fieldset": { borderColor: "rgba(56, 189, 248, 0.22)" },
                  "&:hover fieldset": { borderColor: "#38bdf8" },
                  "&.Mui-focused fieldset": { borderColor: "#38bdf8" },
                },
                "& .MuiInputLabel-root": { color: "#94a3b8" },
              }}
            />
          </Box>

          {/* Submit Action */}
          <Box display="flex" justifyContent="flex-end" gap={1.5} mb={3}>
            <Button
              variant="contained"
              type="submit"
              disabled={tokenBalance === 0}
              sx={{
                bgcolor:
                  tokenBalance === 0
                    ? "rgba(239, 68, 68, 0.2)"
                    : "#0284c7",
                color:
                  tokenBalance === 0
                    ? "#fca5a5"
                    : "#fff",
                fontWeight: 700,
                textTransform: "none",
                px: 3.5,
                py: 1.2,
                borderRadius: "10px",
                boxShadow:
                  tokenBalance === 0
                    ? "none"
                    : "0 0 25px rgba(2, 132, 199, 0.4)",
                "&:hover": {
                  bgcolor:
                    tokenBalance === 0
                      ? "rgba(239, 68, 68, 0.3)"
                      : "#0369a1",
                },
                "&.Mui-disabled": {
                  bgcolor: "rgba(239, 68, 68, 0.2)",
                  color: "#f87171",
                },
              }}
            >
              {tokenBalance === 0
                ? "Insufficient Funds — Top Up Required"
                : "Upload & Crunch Statement"}
            </Button>
          </Box>
        </form>
      )}

      {/* Pay / Top Up Balance Dialog */}
      <PayFinsightsDialog
        open={payDialogOpen}
        onClose={() => setPayDialogOpen(false)}
        onPaid={() => {
          loadBalance();
          Swal.fire({
            icon: "info",
            title: "M-Pesa STK Push Sent",
            text: "Please enter your PIN on your phone to complete payment. Your FinSights balance will update shortly.",
          });
        }}
      />
    </Box>
  );
};

export default UploadStatementForm;
